import Sidebar from 'components/container-components/navbar/Sidebar';
import { NextPage } from 'next';
import Link from 'next/link';
import { APAROnboardingWrapper } from '../APAROnboardingWrapper';
import { useEffect } from 'react';
import { useNavigation } from '../navbar/NavigationContext';
import { useRouter } from 'next/router';
import { AdminRoutes, CommonRoutes } from 'routes';
import { useUser } from 'context/user';
import BankDisclosure from 'components/generic-components/BankDisclosure';
import { useIsNativeApp } from 'hooks/useIsNativeApp';
type PageConfig = {
  isNarrowWidth?: boolean;
  hasSidebar?: boolean;
  isMercoa?: boolean;
  /** Edge-to-edge content with no max-width/padding and no footer (e.g. the org chart canvas). */
  fullBleed?: boolean;
};
export const PageSkeletonLoader = () => (
  <div className="my-6 flex animate-pulse flex-col gap-y-6">
    <div className="flex w-full items-center space-x-4">
      <div className="h-6 w-6 rounded-md bg-neutral-200"></div>
      <div className="h-4 w-1/2 rounded-md bg-neutral-200"></div>
    </div>
  </div>
);
const withLayout =
  (PageComponent: NextPage, pageConfig: PageConfig = {}) =>
  (props: any) => {
    const { isNarrowWidth, hasSidebar, isMercoa, fullBleed } = pageConfig || {};
    const { setHasSidebar, isSidebarCollapsed } = useNavigation();
    const router = useRouter();
    const { roleType } = useUser();
    const isNative = useIsNativeApp();
    const isHomePage = router.pathname === CommonRoutes.Dashboard;
    const isCopilot = router.pathname === AdminRoutes.AI;
    // Bank partnership disclosure (Thera is a fintech, not a bank — services via OMB Bank).
    // Surfaced on all admin pages (managers/admins) in the footer.
    const showBankDisclosure = roleType === 'manager';
    // Set hasSidebar in navigation context once during mount
    useEffect(() => {
      setHasSidebar(hasSidebar || false);
    }, [hasSidebar, setHasSidebar]); // These deps are stable
    return (
      <>
        <Sidebar />
        <div
          className={`block min-h-max min-w-0 flex-1 grid-cols-1 grid-rows-1 overflow-x-clip transition-all duration-300 ease-in-out md:grid ${
            hasSidebar
              ? `ml-[0px] ${
                  isSidebarCollapsed ? 'lg:ml-[80px]' : 'lg:ml-[244px]'
                } lg:mt-[0px] ${fullBleed ? '' : 'lg:px-4 xl:px-6 2xl:px-16'}`
              : ''
          }`}
        >
          <div
            className={`${
              isCopilot || fullBleed
                ? 'mt-0 w-full'
                : `relative mx-auto min-h-[calc(100vh-165px)] w-full px-2 pb-8 pt-8 md:min-h-max md:px-4 md:pb-0 md:pt-8 ${
                    isNarrowWidth ? 'md:max-w-screen-lg' : 'overflow-x-clip md:max-w-screen-xl xl:max-w-screen-2xl'
                  }`
            }`}
          >
            {isMercoa ? (
              <APAROnboardingWrapper>
                <PageComponent {...props} />
              </APAROnboardingWrapper>
            ) : (
              <PageComponent {...props} />
            )}
          </div>
          <div
            className={`mt-auto w-full pb-24 pt-4 text-right text-xs text-neutral-500 lg:pb-8 ${
              isCopilot || fullBleed ? 'hidden' : ''
            }`}
          >
            {showBankDisclosure && (
              <div className={`mb-2 flex justify-end pr-20 ${isHomePage ? '' : 'md:pr-4'}`}>
                <BankDisclosure className="text-right" />
              </div>
            )}
            <div className={`flex items-center justify-end gap-2 pr-20 ${isHomePage ? '' : 'md:pr-4'}`}>
              <span>&copy; {new Date().getFullYear()} Thera</span>
              <a
                href="https://intercom.help/thera/en/"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:font-medium hover:text-black"
              >
                Help Center
              </a>
              {/* Native links to the in-app Terms page; opening getthera.com/terms
                  in-app surfaces the marketing site's ad-cookie banner, which
                  Apple flags as tracking (Guideline 5.1.2(i)). Web keeps the
                  getthera.com link. */}
              {isNative ? (
                <Link href={CommonRoutes.Terms} className="underline hover:font-medium hover:text-black">
                  Terms
                </Link>
              ) : (
                <a
                  href="https://www.getthera.com/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:font-medium hover:text-black"
                >
                  Terms
                </a>
              )}
            </div>
          </div>
        </div>
      </>
    );
  };
export default withLayout;
